import React, { useState, useCallback, useMemo } from 'react'
import { Folder, FolderOpen, ChevronRight, ChevronDown } from '@/ui/icons'
import { WaypointFolderTree } from '@/shared/api/waypointFolders'
import { WaypointResponse } from '@/shared/api/routes'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@/ui/components'
import { cn } from '@/shared/utils'

interface MoveWaypointToFolderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  waypoint: WaypointResponse | null
  folders: WaypointFolderTree[]
  onMove: (waypointId: string, folderId: string | null) => Promise<void>
  onSuccess?: () => void
}

interface FolderNodeProps {
  folder: WaypointFolderTree
  selectedFolderId: string | null
  currentFolderId: string | null
  onSelect: (folderId: string | null) => void
  depth?: number
  expandedFolders: Set<string>
  onToggleExpand: (folderId: string) => void
}

function FolderNode({
  folder,
  selectedFolderId,
  currentFolderId,
  onSelect,
  depth = 0,
  expandedFolders,
  onToggleExpand,
}: FolderNodeProps) {
  const isExpanded = expandedFolders.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const isCurrent = currentFolderId === folder.id

  const handleClick = () => {
    onSelect(folder.id)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleExpand(folder.id)
  }

  return (
    <div>
      <div
        onClick={handleClick}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors',
          'hover:bg-gray-100',
          isSelected && 'bg-adventure-100 border border-adventure-300',
          isCurrent && !isSelected && 'bg-adventure-50',
          depth > 0 && 'ml-4'
        )}
        style={{ marginLeft: `${depth * 16}px` }}
      >
        {/* Expand/collapse button */}
        {folder.children.length > 0 ? (
          <button
            onClick={handleToggle}
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className="w-4 h-4" />
        )}

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="w-4 h-4 text-adventure-600 flex-shrink-0" />
        ) : (
          <Folder className="w-4 h-4 text-adventure-600 flex-shrink-0" />
        )}

        {/* Folder name */}
        <span className="flex-1 truncate">
          {folder.name}
          {folder.waypoint_count !== undefined && (
            <span className="ml-2 text-gray-400 text-xs">
              ({folder.waypoint_count})
            </span>
          )}
          {isCurrent && (
            <span className="ml-2 text-xs text-adventure-600 font-medium">
              (current)
            </span>
          )}
        </span>
      </div>

      {/* Children */}
      {isExpanded && folder.children.length > 0 && (
        <div className="mt-1">
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              selectedFolderId={selectedFolderId}
              currentFolderId={currentFolderId}
              onSelect={onSelect}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function MoveWaypointToFolderDialog({
  open,
  onOpenChange,
  waypoint,
  folders,
  onMove,
  onSuccess,
}: MoveWaypointToFolderDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [isMoving, setIsMoving] = useState(false)

  // Initialize selected folder to current folder when dialog opens
  React.useEffect(() => {
    if (open && waypoint) {
      setSelectedFolderId(waypoint.folder_id || null)
      // Auto-expand folders to show current folder
      if (waypoint.folder_id) {
        expandPathToFolder(waypoint.folder_id, folders)
      }
    }
  }, [open, waypoint, folders])

  // Expand path to a specific folder
  const expandPathToFolder = useCallback((folderId: string, folderList: WaypointFolderTree[]) => {
    const findPath = (id: string, list: WaypointFolderTree[], path: string[] = []): string[] | null => {
      for (const folder of list) {
        if (folder.id === id) {
          return [...path, folder.id]
        }
        if (folder.children.length > 0) {
          const found = findPath(id, folder.children, [...path, folder.id])
          if (found) return found
        }
      }
      return null
    }

    const path = findPath(folderId, folderList)
    if (path) {
      setExpandedFolders((prev) => {
        const next = new Set(prev)
        path.forEach((id) => next.add(id))
        return next
      })
    }
  }, [])

  const handleToggleExpand = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  const handleSelectRoot = useCallback(() => {
    setSelectedFolderId(null)
  }, [])

  const handleMove = useCallback(async () => {
    if (!waypoint) return

    try {
      setIsMoving(true)
      await onMove(waypoint.id, selectedFolderId)
      onOpenChange(false)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsMoving(false)
    }
  }, [waypoint, selectedFolderId, onMove, onOpenChange, onSuccess])

  const selectedFolderName = useMemo(() => {
    if (selectedFolderId === null) return 'No Folder'
    
    const findFolder = (id: string, list: WaypointFolderTree[]): WaypointFolderTree | null => {
      for (const folder of list) {
        if (folder.id === id) return folder
        if (folder.children.length > 0) {
          const found = findFolder(id, folder.children)
          if (found) return found
        }
      }
      return null
    }

    const folder = findFolder(selectedFolderId, folders)
    return folder?.name || 'Unknown Folder'
  }, [selectedFolderId, folders])

  if (!waypoint) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Move Waypoint to Folder</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          <div className="space-y-1">
            {/* Root option */}
            <div
              onClick={handleSelectRoot}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors',
                'hover:bg-gray-100',
                selectedFolderId === null && 'bg-adventure-100 border border-adventure-300',
                waypoint.folder_id === null && selectedFolderId !== null && 'bg-adventure-50'
              )}
            >
              <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1">
                No Folder
                {waypoint.folder_id === null && (
                  <span className="ml-2 text-xs text-adventure-600 font-medium">
                    (current)
                  </span>
                )}
              </span>
            </div>

            {/* Folder tree */}
            {folders.map((folder) => (
              <FolderNode
                key={folder.id}
                folder={folder}
                selectedFolderId={selectedFolderId}
                currentFolderId={waypoint.folder_id || null}
                onSelect={setSelectedFolderId}
                depth={0}
                expandedFolders={expandedFolders}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMoving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={isMoving || selectedFolderId === waypoint.folder_id}
          >
            {isMoving ? 'Moving...' : `Move to ${selectedFolderName}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

